/**
 * Profile Questionnaire — page wrapper.
 *
 * Thin shell that:
 *   - Resolves the current registration kind from the auth store.
 *   - Decides mode (CREATE vs EDIT) from the URL `?mode=edit`, OR
 *     from a server probe when arriving without `?mode=edit` (see
 *     RESUME-STATE below).
 *   - Mounts the shared `useProfileQuestionnaireProgram` hook.
 *   - Renders the S005 Step3TransitionScreen after a successful CREATE.
 *   - Hands the model + callbacks to `ProfileQuestionnaireView`.
 *
 * The same page is used for /onboarding (CREATE) and
 * /onboarding?mode=edit (EDIT, e.g. from S018's "Редактировать профиль").
 *
 * RESUME-STATE (live round-trip recovery, see
 * ACT-INDEEP-PROFILE-QUESTIONNAIRE-LIVE-ROUNDTRIP-RECOVERY01 +
 * CORRECTION01 of the same ACT):
 *
 *   Without `?mode=edit`, the page historically always mounted in
 *   CREATE mode and rendered an empty form, even when the user
 *   already had a saved profile. That made the questionnaire look
 *   like it "lost" the saved data on hard reload.
 *
 *   The fix here is a server probe at mount: when arriving without
 *   `?mode=edit`, we issue ONE GET against the canonical profile
 *   endpoint (getFriendProfile / getSponsorProfile) and switch to
 *   EDIT mode iff the server reports a profile. The probe is
 *   re-evaluated on EVERY change to (requestedEdit, accountId,
 *   registrationKind) so the result cannot go stale across
 *   logout/login or registration-kind transitions.
 *
 *   Convergence matrix (every branch explicitly resolves to one
 *   of {resolving, create, edit}; nothing is left implicit / sticky):
 *
 *     requestedEdit=true                  -> edit (URL wins, no probe)
 *     accountId === null                  -> resolving (auth gate above)
 *     kind ∉ {friend, sponsor}            -> create (no canonical profile)
 *     probe 200 + non-null                -> edit
 *     probe 404 / null                    -> create
 *     probe transient error (5xx, net)    -> create (with console.warn;
 *                                           absence vs unknown are
 *                                           semantically different; a
 *                                           dedicated retry surface is
 *                                           deferred to a follow-up ACT)
 *
 *   INTERMEDIATE-STATE SAFETY (CORRECTION01 round 3):
 *     On a fresh mount without `?mode=edit`, the page used to
 *     commit an initial `useState('create')` render before the
 *     async probe flipped to `'edit'` for users with a saved
 *     profile. That produced a visible CREATE flash, and on
 *     identity changes could transiently mount the wrong program
 *     for the new identity. The fix binds the resolution to a
 *     `resolutionKey` derived from the inputs (`accountId:
 *     registrationKind: requestedEdit`); the `effectiveMode` is
 *     DERIVED from that key matching the last stored resolution,
 *     so the moment any input changes the old resolution is
 *     structurally invalid and the page enters `'resolving'`
 *     (no program mount) until a fresh resolution arrives. The
 *     effect is now only the async resolver; it does not need to
 *     "reset" `effectiveMode` because the derivation already does.
 *
 *   Probe ordering: re-runs are guarded by a cancellation flag
 *   (React's standard async-effect pattern) so an in-flight probe
 *   for an older (accountId, kind) tuple cannot overwrite a newer
 *   resolution.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useProfileQuestionnaireProgram } from '@/features/profile-questionnaire/useProfileQuestionnaireProgram';
import { ProfileQuestionnaireView } from '@/features/profile-questionnaire/view';
import type { ProfileFormMode } from '@/features/profile-questionnaire/model';
import { RoutePaths } from '@/routes';
import { browserObjectUrlPort } from '@/api/objectUrlPort';
import {
  getFriendProfile,
  getSponsorProfile,
} from '@/api/profiles';

export function ProfileQuestionnairePage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const registrationKind = useAuthStore((s) => s.registrationKind);
  const accountId = useAuthStore((s) => s.accountId);
  const authStatus = useAuthStore((s) => s.authStatus);

  // RESUME-STATE (live round-trip recovery):
  //   When the user arrives at /profile-questionnaire without
  //   ?mode=edit but a profile already exists on the server, we
  //   probe the GET endpoint and switch effectiveMode to 'edit' so
  //   the form is hydrated with the saved data instead of rendering
  //   an empty CREATE form. The explicit ?mode=edit URL always
  //   wins (no probe). For non-friend/non-sponsor kinds
  //   (unclassified, empty), we skip the probe and stay in CREATE.
  //
  //   CONVERGENCE (CORRECTION01): every dependency change
  //   (requestedEdit, accountId, registrationKind) deterministically
  //   re-derives effectiveMode in BOTH directions. Earlier revisions
  //   only transitioned create->edit and could leave EDIT mode
  //   sticky across logout/login or a kind change away from
  //   friend/sponsor. See the convergence matrix in the file header.
  //
  //   The hooks below MUST be called unconditionally (before any
  //   early return); they gate on `accountId !== null` internally.
  const requestedEdit = search.get('mode') === 'edit';
  const resolutionKey = `${accountId ?? 'anon'}:${registrationKind}:${requestedEdit}`;
  const [resolution, setResolution] = useState<{
    readonly key: string;
    readonly mode: 'create' | 'edit';
  } | null>(null);
  // Derived mode. URL wins; otherwise the resolution is valid only
  // when its key matches the current inputs.
  const effectiveMode: 'resolving' | 'create' | 'edit' = requestedEdit
    ? 'edit'
    : resolution !== null && resolution.key === resolutionKey
      ? resolution.mode
      : 'resolving';
  useEffect(() => {
    // 1) Explicit ?mode=edit always wins. No probe; no resolution
    //    update needed (the derivation already returns 'edit').
    if (requestedEdit) {
      return;
    }
    // 2) Auth bootstrap not done; defer until accountId resolves.
    //    The auth-bootstrap gate above will keep us out of the
    //    program mount path until accountId is canonical.
    if (accountId === null) {
      return;
    }
    // 3) Non-friend / non-sponsor kinds have no canonical profile.
    //    Resolve to CREATE synchronously under the current key.
    if (registrationKind !== 'friend' && registrationKind !== 'sponsor') {
      setResolution({ key: resolutionKey, mode: 'create' });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const profile =
          registrationKind === 'friend'
            ? await getFriendProfile()
            : await getSponsorProfile();
        if (cancelled) return;
        // 200 + non-null -> edit; 404 / null -> create.
        // The key is bound to the inputs at the time of resolution,
        // so a later input change makes this resolution invalid.
        setResolution({
          key: resolutionKey,
          mode: profile ? 'edit' : 'create',
        });
      } catch (err) {
        // Transient probe failure (5xx, network, abort). Absence and
        // inability-to-determine-absence are semantically different,
        // but exposing a dedicated retry surface is deferred to a
        // follow-up ACT (out of scope here). For now: log so the
        // failure is observable in the console, and resolve to
        // CREATE so the user can still complete the form. When
        // (accountId, kind) changes the probe re-runs naturally.
        // eslint-disable-next-line no-console
        console.warn(
          '[profile-questionnaire] resume probe failed; falling back to CREATE:',
          err
        );
        if (cancelled) return;
        setResolution({ key: resolutionKey, mode: 'create' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestedEdit, registrationKind, accountId, resolutionKey]);
  const programKey = `${accountId ?? 'anon'}:${effectiveMode}`;

  // C1 (CORRECTION03): gate the questionnaire program mount on
  // resolved auth context. Without this gate, the program mounts
  // with accountId=null and initializes a CREATE-derived reducer
  // state; when accountId resolves async, useReducer's initState
  // does NOT re-run, and the form is stuck in
  // `data-mode="create"`, `form.gender=undefined` until a hard
  // reload re-runs the initializer. By not mounting the program
  // until accountId is canonical, we make the reducer's first
  // observation of `mode` correct on the first render, with no
  // possibility of a CREATE-state/EDIT-prop split.
  if (
    authStatus === 'loading' ||
    authStatus === 'unknown' ||
    accountId === null
  ) {
    return (
      <div
        className="page"
        data-testid="pq-auth-loading"
        data-auth-status={authStatus}
      >
        <p>Загрузка...</p>
      </div>
    );
  }

  // authStatus === 'authenticated' and accountId is non-null here.
  // The child component computes `mode` and mounts the program; its
  // first (and only, due to the `key` below) init observes the
  // canonical mode. The `key` is the resolved program identity so
  // any future context re-resolution (e.g. logout/login as a
  // different account) deliberately remounts; ordinary rerenders
  // do NOT change the key, so dirty state is preserved.

  // INTERMEDIATE-STATE SAFETY (CORRECTION01 round 3): do NOT mount
  // the program while the resolution is still pending. The previous
  // implementation committed an initial CREATE-mode render before
  // the async probe could flip to EDIT, producing a visible flash
  // for users with a saved profile and a stale-EDIT intermediate
  // on identity changes. Show a `pq-resolving` shell instead so
  // the program only mounts with the canonical mode on its first
  // observation.
  if (effectiveMode === 'resolving') {
    return (
      <div
        className="page"
        data-testid="pq-resolving"
        data-resolution-key={resolutionKey}
      >
        <p>Загрузка...</p>
      </div>
    );
  }

  return (
    <ProfileQuestionnaireProgramHost
      key={programKey}
      accountId={accountId}
      requestedEdit={effectiveMode === 'edit'}
      registrationKind={registrationKind}
      onNavigateDiscover={() => navigate(RoutePaths.DISCOVER)}
    />
  );
}

interface ProfileQuestionnaireProgramHostProps {
  readonly accountId: string;
  readonly requestedEdit: boolean;
  readonly registrationKind: ReturnType<typeof useAuthStore.getState>['registrationKind'];
  readonly onNavigateDiscover: () => void;
}

/**
 * Hosts the questionnaire program only when canonical context is
 * available. The parent (ProfileQuestionnairePage) gates on auth
 * resolution and passes a stable `key` derived from the resolved
 * account id + requested mode. Because this component never sees
 * accountId=null, the program hook always initializes with the
 * correct mode on first mount.
 */
function ProfileQuestionnaireProgramHost(
  props: ProfileQuestionnaireProgramHostProps
): React.ReactNode {
  const { accountId, requestedEdit, registrationKind, onNavigateDiscover } =
    props;

  const mode: ProfileFormMode = useMemo(
    () =>
      requestedEdit
        ? { kind: 'edit', profileId: accountId }
        : { kind: 'create' },
    [requestedEdit, accountId]
  );

  const program = useProfileQuestionnaireProgram({ mode, registrationKind });

  // Trigger load on mount. The empty-deps effect is safe because
  // the parent's `key` remounts this component on any identity
  // change. B-2.C skills loads are emitted by the `Started` reducer
  // itself (alongside LoadExistingProfile for EDIT mode), so this
  // effect only needs to call `start()`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    program.start();
  }, []);

  // S005 transition handling: when the S005 transition is shown in
  // CREATE mode, the user clicks Start → navigate to the next step
  // (Discover). For EDIT mode, the S005 transition is never shown.
  const showS005 = mode.kind === 'create' && program.model.s005TransitionShown;

  if (showS005) {
    return (
      <S005TransitionScreen
        onStart={() => {
          program.s005StartRequested();
          onNavigateDiscover();
        }}
      />
    );
  }

  return (
    <ProfileQuestionnaireView
      model={program.model}
      onNameChanged={program.nameChanged}
      onGenderChanged={program.genderChanged}
      onScenarioChanged={program.scenarioChanged}
      onAgeChanged={program.ageChanged}
      onCityChanged={program.cityChanged}
      onHeadlineChanged={program.headlineChanged}
      onBioChanged={program.bioChanged}
      onWorkSphereChanged={program.workSphereChanged}
      onEducationSphereChanged={program.educationSphereChanged}
      onEducationTypeChanged={program.educationTypeChanged}
      // B-2.B photo surface:
      objectUrlPort={browserObjectUrlPort}
      onPhotoFilesSelected={program.photoFilesSelected}
      onPhotoDeleteRequested={program.photoDeleteRequested}
      onPhotoPreviewReleased={program.photoPreviewReleased}
      onPhotoUploadRequested={program.photoUploadRequested}
      onSubmit={program.submit}
      onRetry={program.retry}
      onDismissCompletionModal={program.dismissCompletionModal}
      // B-2.C skills:
      onSkillsTaxonomyLoad={program.skillsTaxonomyLoadRequested}
      onSkillsProfileLoad={program.skillsProfileLoadRequested}
      onSkillSelected={program.skillSelected}
      onSkillRemoved={program.skillRemoved}
      onSkillsSearchChanged={program.skillsSearchChanged}
      onSkillsCategoryChanged={program.skillsCategoryChanged}
      onSkillsSaveRequested={program.skillsSaveRequested}
    />
  );
}

/**
 * S005 Step3TransitionScreen — Figma node 38:615.
 *
 * Live-Figma authority for the canonical copy:
 *   - "Отлично!"
 *   - "Регистрация почти завершена."
 *   - "3 минуты"
 *   - "Начать" CTA
 *
 * Rendered after a successful CREATE → active transition per the
 * Bucket-B briefing §34. The transition is shown BEFORE the
 * congratulations modal (the modal is dismissible; the transition is
 * the canonical Figma authority for the journey between S004 and the
 * questionnaire completion).
 */
function S005TransitionScreen({ onStart }: { onStart: () => void }): React.ReactNode {
  return (
    <section
      className="pq-s005"
      data-testid="pq-s005-transition"
      aria-labelledby="pq-s005-title"
    >
      <h1 id="pq-s005-title" className="pq-s005-title">Отлично!</h1>
      <p className="pq-s005-body">Регистрация почти завершена.</p>
      <p className="pq-s005-time">3 минуты</p>
      <button
        type="button"
        className="pq-s005-start"
        onClick={onStart}
        data-testid="pq-s005-start"
      >
        Начать
      </button>
    </section>
  );
}
